import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { API_POST } from "../../services/api.service";
import { getTeamDetails } from "../common/helper";
import { createHistoryEntry, mergeHistoryEntries } from "./ticketHistoryConfig";

const SupportDevMappingPopup = forwardRef(({ open, onClose, onSuccess, supportId, user, inline, style = {}, cardProps = {}, setMapping }, ref) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
  const [configData, setConfigData] = useState({
    modules: [],
    categories: [],
    subcategories: []
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [mappingData, setMappingData] = useState({
    module: '',
    category: ''
  });
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Ticket status options
  const developmentStatusOptions = [
    "In-Progress",
    "In-Roadmap",
    "Under Development",
    "Under QA Testing",
    "Resolved",
    "Reopen"
  ];

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    if (mappingData.module) {
      // Filter tickets by module
      const filtered = tickets.filter(ticket =>
        ticket.module === mappingData.module
      );
      // Extract unique categories
      const uniqueCategories = Array.from(new Set(filtered.map(ticket => ticket.category).filter(Boolean)));
      setCategoryOptions(uniqueCategories);
      // If current category is not in the new options, reset it
      if (!uniqueCategories.includes(mappingData.category)) {
        setMappingData(prev => ({ ...prev, category: '' }));
      }
    } else {
      setCategoryOptions([]);
      setMappingData(prev => ({ ...prev, category: '' }));
    }
  }, [mappingData.module, tickets]);

  // Auto-select the first ticket after filtering, or let user pick if multiple
  useEffect(() => {
    if (mappingData.module && mappingData.category) {
      const filtered = tickets.filter(ticket =>
        ticket.module === mappingData.module &&
        ticket.category === mappingData.category
      );
      if (filtered.length === 1) {
        setSelectedTicket(filtered[0]);
        if (setMapping) setMapping(filtered[0]);
      } else if (filtered.length > 1) {
        // Do not auto-select, require user selection
        setSelectedTicket(null);
        if (setMapping) setMapping(null);
      } else {
        setSelectedTicket(null);
        if (setMapping) setMapping(null);
      }
    } else {
      setSelectedTicket(null);
      if (setMapping) setMapping(null);
    }
  }, [mappingData.module, mappingData.category, tickets]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load configuration data
      const configRes = await API_POST("action/getdata", { 
        root: "systemSettingsConfiguration", 
        con: { context: "supportDevConfig" } 
      });
      
      if (configRes && configRes.data) {
        const config = {
          types: [],
          modules: [],
          categories: [],
          subcategories: []
        };
        
        configRes.data.forEach(item => {
          if (item.name && item.value) {
            config[item.name] = item.value;
          }
        });
        setConfigData(config);
      }

      // Load team members
      const teamData = getTeamDetails();
      if (teamData && typeof teamData === 'object') {
        const members = Object.keys(teamData).map(email => {
          const member = teamData[email];
          return member.username || member.first_name || email;
        }).filter(Boolean);
        setTeamMembers(members);
      }

      // Load tickets
      const ticketsRes = await API_POST("action/getdata", { 
        root: "supportDevTickets", 
        con: {} 
      });
      
      if (ticketsRes && ticketsRes.data) {
        setTickets(ticketsRes.data);
      }

    } catch (err) {
      console.error("Error loading data:", err);
      setSnackbar({ open: true, message: 'Error loading data', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMappingDataChange = (field, value) => {
    setMappingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
    if (setMapping) setMapping(ticket);
  };

  const handleSubmit = async (e) => {
    if (!selectedTicket) {
      setSnackbar({ open: true, message: 'Please select a ticket', color: 'error' });
      return;
    }

    // Check if supportId is already mapped to this ticket
    const existingMappedIds = selectedTicket.mappedId || [];
    if (existingMappedIds.includes(supportId)) {
      setSnackbar({ open: true, message: 'This support ticket is already mapped to the selected dev ticket', color: 'warning' });
      onSuccess();
      return;
    }

    setLoading(true);
    try {
      const currentTime = new Date().toISOString();
      
      // Create history entry for mapping changes
      const mappingHistoryEntry = createHistoryEntry(
        'mapped',
        'mappedId',
        selectedTicket.mappedId || [],
        [...(selectedTicket.mappedId || []), supportId],
        user
      );
      
      // Merge with existing history
      const existingHistory = selectedTicket.history || [];
      const updatedHistory = mergeHistoryEntries(existingHistory, [mappingHistoryEntry]);
      
      const updateData = {
        ...selectedTicket,
        mappedId: [...(selectedTicket.mappedId || []), supportId],
        admin: user.email || '',
        username: user.firstName || user.username || '',
        updatedAt: currentTime,
        history: updatedHistory
      };

      await API_POST("action/update", {
        root: "supportDevTickets",
        querydata: { _id: selectedTicket._id },
        body: updateData
      });

      setSnackbar({ open: true, message: 'Ticket mapped successfully', color: 'success' });
      
      // Update local state
      setTickets(prev => prev.map(t => 
        t._id === selectedTicket._id ? { ...t, mappedId: updateData.mappedId, history: updatedHistory } : t
      ));
      
      // Call success callback to refresh parent component
      if (onSuccess) {
        onSuccess();
      }
      
      // Reset form
      setMappingData({
        module: '',
        category: ''
      });
      setSelectedTicket(null);
      
      // Close popup after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error("Error mapping ticket:", err);
      setSnackbar({ open: true, message: 'Error mapping ticket', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    setLoading(true);
    try {
      const ticket = tickets.find(t => t._id === ticketId);
      if (!ticket) return;

      const currentTime = new Date().toISOString();
      
      // Create history entry for status change
      const statusHistoryEntry = createHistoryEntry(
        'status_changed',
        'developmentStatus',
        ticket.developmentStatus,
        newStatus,
        user
      );
      
      // Merge with existing history
      const existingHistory = ticket.history || [];
      const updatedHistory = mergeHistoryEntries(existingHistory, [statusHistoryEntry]);
      
      const updateData = {
        ...ticket,
        developmentStatus: newStatus,
        admin: user.email || '',
        username: user.firstName || user.username || '',
        updatedAt: currentTime,
        history: updatedHistory
      };

      await API_POST("action/update", {
        root: "supportDevTickets",
        querydata: { _id: ticketId },
        body: updateData
      });

      // Update local state
      setTickets(prev => prev.map(t => 
        t._id === ticketId ? { ...t, developmentStatus: newStatus, history: updatedHistory } : t
      ));

      setSnackbar({ open: true, message: 'Status updated successfully', color: 'success' });
      
    } catch (err) {
      console.log("Error updating status:", err);
      setSnackbar({ open: true, message: 'Error updating status', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMappingData({
      module: '',
      category: ''
    });
    setSelectedTicket(null);
    onClose();
  };

  // Check if all required fields are selected
  const allFieldsSelected = mappingData.module && mappingData.category;

  const filteredTickets = allFieldsSelected ? tickets.filter(ticket => {
    if (mappingData.module && ticket.module !== mappingData.module) return false;
    if (mappingData.category && ticket.category !== mappingData.category) return false;
    return true;
  }) : [];

  useImperativeHandle(ref, () => ({
    saveMapping: handleSubmit
  }));

  // Render form content
  const formContent = (
    <Box width="100%" style={{ ...style, maxWidth: '100%', background: inline ? 'transparent' : '#f9f9f9', borderRadius: inline ? 0 : 12, padding: inline ? 0 : 8 }}>
      <Typography variant="h6" gutterBottom style={{ fontWeight: 600, color: '#1976d2', marginBottom: 12, fontSize: 16 }}>
        Map to Development Ticket
      </Typography>
      <Grid container spacing={1}>
        {/* Removed Type field */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }}>
            <InputLabel size="small">Module</InputLabel>
            <Select
              value={mappingData.module}
              onChange={e => handleMappingDataChange('module', e.target.value)}
              label="Module"
              size="small"
              style={{ fontSize: 14, minHeight: 36 }}
            >
              {configData.modules.map(module => (
                <MenuItem key={module} value={module} style={{ fontSize: 14, minHeight: 32 }}>{module}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }}>
            <InputLabel size="small">Category</InputLabel>
            <Select
              value={mappingData.category}
              onChange={e => handleMappingDataChange('category', e.target.value)}
              label="Category"
              size="small"
              style={{ fontSize: 14, minHeight: 36 }}
            >
              {categoryOptions.map(category => (
                <MenuItem key={category} value={category} style={{ fontSize: 14, minHeight: 32 }}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Show filtered tickets in a grid and auto-select the first */}
        {allFieldsSelected && filteredTickets.length > 1 && (
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }}>
              <InputLabel size="small">Select Ticket</InputLabel>
              <Select
                value={selectedTicket ? selectedTicket._id : ''}
                onChange={e => {
                  const value = e.target.value;
                  if (!value) {
                    handleTicketSelect(null);
                  } else {
                    const ticket = filteredTickets.find(t => t._id === value);
                    handleTicketSelect(ticket);
                  }
                }}
                label="Select Ticket"
                size="small"
                style={{ fontSize: 14, minHeight: 36 }}
              >
                <MenuItem value="" style={{ fontSize: 14, fontStyle: 'italic', color: '#888', minHeight: 32 }}>None</MenuItem>
                {filteredTickets.map(ticket => (
                  <MenuItem key={ticket._id} value={ticket._id} style={{ fontSize: 14, minHeight: 32 }}>
                    {ticket.description || ticket._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

          {/* Show selected ticket details and status update */}
          {selectedTicket && (
            <Grid item xs={12} sm={12}>
              <Card variant="outlined" style={{ marginBottom: 8, padding: 0, boxShadow: 'none' }}>
                <CardContent style={{ padding: 8 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 4, fontSize: 15 }}>Ticket Details</Typography>
                  {selectedTicket.description && (
                    <Typography variant="caption" style={{ marginTop: 2, fontSize: 12 }}><b>Description:</b> {selectedTicket.description}</Typography>
                  )}<br/>
                  {selectedTicket.priority && (
                    <Typography variant="caption" style={{ marginTop: 2, fontSize: 12 }}><b>Priority:</b> {selectedTicket.priority}</Typography>
                  )}<br/>
                  <Typography variant="caption" style={{ marginTop: 2, fontSize: 12 }}><b>Status:</b> {selectedTicket.developmentStatus}</Typography>
                  {/* Comments */}<br/>
                  {selectedTicket.comments && Array.isArray(selectedTicket.comments) && selectedTicket.comments.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" style={{ fontWeight: 500, fontSize: 12 }}>Comments:</Typography>
                      {selectedTicket.comments.map((comment, idx) => (
                        <Typography key={idx} variant="caption" color="textSecondary" style={{ marginLeft: 6, fontSize: 12 }}>
                          - {comment}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {/* Status update dropdown - only show if status is Resolved */}
                  {selectedTicket.developmentStatus === 'Resolved' && (
                    <FormControl variant="outlined" size="small" style={{ marginTop: 6, minWidth: 120 }}>
                      <InputLabel size="small">Update Status</InputLabel>
                      <Select
                        value={selectedTicket.developmentStatus || ''}
                        onChange={e => handleStatusUpdate(selectedTicket._id, e.target.value)}
                        label="Update Status"
                        size="small"
                        style={{ fontSize: 13, minHeight: 32 }}
                      >
                        {developmentStatusOptions.map(status => (
                          <MenuItem key={status} value={status} style={{ fontSize: 13, minHeight: 28 }}>{status}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                </CardContent>
              </Card>
            </Grid>
          )}
        {/* Remove Save Mapping button */}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        ContentProps={{ style: { backgroundColor: snackbar.color === 'success' ? '#43a047' : snackbar.color === 'error' ? '#d32f2f' : '#ffa000', fontSize: 14 } }}
      />
    </Box>
  );

  if (inline) {
    return (
      <Box width="100%" style={{ padding: 0, background: 'transparent', boxShadow: 'none', border: 'none', margin: 0 }}>
        {formContent}
      </Box>
    );
  }

  // fallback: keep Dialog for backward compatibility
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Map to Development Ticket</DialogTitle>
      <DialogContent>{formContent}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
});

export default SupportDevMappingPopup; 